import React, { createContext, useState, ReactNode, useEffect, useContext } from 'react';
import { db } from '../firebase';
import { collection, query, onSnapshot, orderBy, doc, addDoc, updateDoc, deleteDoc, where, getDocs, Timestamp, setDoc } from 'firebase/firestore';
import { useAuth } from '../hooks/useAuth';
import { useUserAccess } from './UserAccessContext';
import { KlinikRecord, NewKlinikRecordData, KlinikConfig } from '../types';

export interface KlinikContextType {
    loading: boolean;
    klinikRecords: KlinikRecord[];
    klinikConfig: KlinikConfig | null;

    addKlinikRecord: (data: NewKlinikRecordData) => Promise<string>;
    updateKlinikRecord: (id: string, data: Partial<KlinikRecord>) => Promise<void>;
    deleteKlinikRecord: (id: string) => Promise<void>;
    getSantriKlinikHistory: (santriId: string) => Promise<KlinikRecord[]>;
    cekStatusKlinikAktif: (santriId: string) => Promise<KlinikRecord | null>;
    updateKlinikConfig: (config: KlinikConfig) => Promise<void>;
}

export const KlinikContext = createContext<KlinikContextType | undefined>(undefined);

export const useKlinik = () => {
    const context = useContext(KlinikContext);
    if (context === undefined) {
        throw new Error('useKlinik must be used within a KlinikProvider');
    }
    return context;
};

export const KlinikProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { authUser } = useAuth();
    const { isSuperAdmin, userPermissions, loading: accessLoading } = useUserAccess();

    const [klinikRecords, setKlinikRecords] = useState<KlinikRecord[]>([]);
    const [klinikConfig, setKlinikConfig] = useState<KlinikConfig | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let isMounted = true;
        if (!authUser || accessLoading) {
            if (!authUser) {
                setKlinikRecords([]);
                setKlinikConfig(null);
                setLoading(false);
            }
            return;
        }

        setLoading(true);

        const klinikConfigDocRef = doc(db, 'settings', 'klinik_config');
        const unsubscribeKlinikConfig = onSnapshot(klinikConfigDocRef, (docSnap) => {
            if (isMounted && docSnap.exists()) {
                setKlinikConfig(docSnap.data() as KlinikConfig);
            }
        });

        // Hybrid Provider Logic: Check Permissions
        const permData = userPermissions?.find(p => p.email === authUser?.email);
        const allowedMenus = permData?.allowedMenus || [];
        const hasKlinikAccess = isSuperAdmin || allowedMenus.some(menu => ['klinik', 'admin-klinik'].includes(menu));

        let unsubscribeKlinikRecords: (() => void) | undefined;

        if (hasKlinikAccess) {
            // Mode 1: Global Real-time Listener for Admins
            const sevenDaysAgo = new Date();
            sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

            const klinikRecordsQuery = query(
                collection(db, 'klinik_records'),
                where('tanggalMasuk', '>=', sevenDaysAgo),
                orderBy('tanggalMasuk', 'desc')
            );

            unsubscribeKlinikRecords = onSnapshot(klinikRecordsQuery, snapshot => {
                const recordsData = snapshot.docs.map(doc => {
                    const data = doc.data();
                    return {
                        id: doc.id,
                        ...data,
                        tanggalMasuk: (data.tanggalMasuk as Timestamp).toDate(),
                        tanggalSembuh: (data.tanggalSembuh as Timestamp)?.toDate(),
                        tanggalRujuk: (data.tanggalRujuk as Timestamp)?.toDate(),
                        catatanPerkembangan: data.catatanPerkembangan?.map((c: any) => ({
                            ...c,
                            tanggal: (c.tanggal as Timestamp)?.toDate ? (c.tanggal as Timestamp).toDate() : c.tanggal
                        })),
                    } as KlinikRecord;
                });
                if (isMounted) {
                    setKlinikRecords(recordsData);
                    setLoading(false);
                }
            });
        } else {
            // Mode 2: Idle Mode for Regular Teachers / Parents
            if (isMounted) {
                setKlinikRecords([]);
                setLoading(false);
            }
        }

        return () => {
            isMounted = false;
            unsubscribeKlinikConfig();
            if (unsubscribeKlinikRecords) unsubscribeKlinikRecords();
        };
    }, [authUser, isSuperAdmin, userPermissions, accessLoading]);

    const addKlinikRecord = async (data: NewKlinikRecordData) => {
        // Hapus properties yang bernilai undefined agar Firestore tidak throw Error
        const cleanData = Object.entries(data).reduce((acc, [key, value]) => {
            if (value !== undefined) {
                acc[key] = value;
            }
            return acc;
        }, {} as any);

        const docRef = await addDoc(collection(db, 'klinik_records'), cleanData);
        return docRef.id;
    };

    const updateKlinikRecord = async (id: string, data: Partial<KlinikRecord>) => {
        const cleanData = Object.entries(data).reduce((acc, [key, value]) => {
            if (value !== undefined) {
                acc[key] = value;
            }
            return acc;
        }, {} as any);
        await updateDoc(doc(db, 'klinik_records', id), cleanData);
    };

    const deleteKlinikRecord = async (id: string) => await deleteDoc(doc(db, 'klinik_records', id));

    const cekStatusKlinikAktif = async (santriId: string): Promise<KlinikRecord | null> => {
        // Mode 1: Check memory if we have global data
        if (klinikRecords.length > 0) {
            const memoryRecord = klinikRecords.find(r => r.santriId === santriId && r.status !== 'Sembuh' && r.status !== 'Dirujuk');
            if (memoryRecord) return memoryRecord;
        }

        // Mode 2: Fetch on-demand from database
        const q = query(
            collection(db, 'klinik_records'),
            where('santriId', '==', santriId),
            where('status', 'not-in', ['Sembuh', 'Dirujuk'])
        );
        const snapshot = await getDocs(q);
        if (snapshot.empty) return null;

        const doc = snapshot.docs[0];
        const data = doc.data();
        return {
            id: doc.id,
            ...data,
            tanggalMasuk: (data.tanggalMasuk as Timestamp).toDate(),
            tanggalSembuh: (data.tanggalSembuh as Timestamp)?.toDate(),
            tanggalRujuk: (data.tanggalRujuk as Timestamp)?.toDate(),
            catatanPerkembangan: data.catatanPerkembangan?.map((c: any) => ({
                ...c,
                tanggal: (c.tanggal as Timestamp)?.toDate ? (c.tanggal as Timestamp).toDate() : c.tanggal
            })),
        } as KlinikRecord;
    };

    const getSantriKlinikHistory = async (santriId: string): Promise<KlinikRecord[]> => {
        const q = query(
            collection(db, 'klinik_records'),
            where('santriId', '==', santriId)
        );
        const snapshot = await getDocs(q);
        const records = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
                id: doc.id,
                ...data,
                tanggalMasuk: (data.tanggalMasuk as Timestamp).toDate(),
                tanggalSembuh: (data.tanggalSembuh as Timestamp)?.toDate(),
                tanggalRujuk: (data.tanggalRujuk as Timestamp)?.toDate(),
                catatanPerkembangan: data.catatanPerkembangan?.map((c: any) => ({
                    ...c,
                    tanggal: (c.tanggal as Timestamp)?.toDate ? (c.tanggal as Timestamp).toDate() : c.tanggal
                })),
            } as KlinikRecord;
        });
        return records.sort((a, b) => b.tanggalMasuk.getTime() - a.tanggalMasuk.getTime());
    };

    const updateKlinikConfig = async (config: KlinikConfig) => {
        await setDoc(doc(db, 'settings', 'klinik_config'), config);
    };

    return (
        <KlinikContext.Provider value={{
            loading,
            klinikRecords, klinikConfig,
            addKlinikRecord, updateKlinikRecord, deleteKlinikRecord,
            getSantriKlinikHistory, cekStatusKlinikAktif, updateKlinikConfig
        }}>
            {children}
        </KlinikContext.Provider>
    );
};
